import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { adminLogout } from './adminApi';
import { NotificationProvider, useNotifications } from './NotificationContext';
import { useCompanyInfo } from '../hooks/useCompanyInfo';

const FALLBACK_LOGO = '/images/1-logo/yatay/yatay2.png';
import {
  LayoutDashboard,
  Building2,
  Layers,
  Mail,
  Star,
  Settings,
  LogOut,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

function Badge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#c5a059] text-black text-[10px] font-bold px-1 leading-none">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarNav({ closeSidebar }) {
  const { messages, dreams } = useNotifications();

  const NAV = [
    { to: '/admin/dashboard',       icon: LayoutDashboard, label: 'Dashboard',           badge: 0 },
    { to: '/admin/projects',        icon: Building2,       label: 'Projeler',            badge: 0 },
    { to: '/admin/services',        icon: Layers,          label: 'Hizmetler',           badge: 0 },
    { to: '/admin/messages',        icon: Mail,            label: 'Mesajlar',            badge: messages },
    { to: '/admin/dream-requests',  icon: Star,            label: 'Hayalim Talepleri',   badge: dreams },
    { to: '/admin/company-info',    icon: Settings,        label: 'Şirket Bilgileri',    badge: 0 },
  ];

  return (
    <nav className="flex-1 px-2.5 py-3 space-y-0.5 overflow-y-auto">
      {NAV.map(({ to, icon: Icon, label, badge }) => (
        <NavLink
          key={to}
          to={to}
          onClick={closeSidebar}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${
              isActive
                ? 'bg-[#c5a059]/10 text-[#c5a059] border border-[#c5a059]/20'
                : 'text-white/45 hover:text-white/80 hover:bg-white/[0.04] border border-transparent'
            }`
          }
        >
          <Icon size={15} className="flex-shrink-0" />
          <span className="flex-1">{label}</span>
          <Badge count={badge} />
          <ChevronRight
            size={11}
            className={`opacity-0 group-hover:opacity-30 transition-opacity ${badge ? 'hidden' : ''}`}
          />
        </NavLink>
      ))}
    </nav>
  );
}

function AdminLayoutInner() {
  const { logout } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logo_url } = useCompanyInfo();
  const logoSrc = logo_url?.trim() || FALLBACK_LOGO;

  const handleLogout = async () => {
    try { await adminLogout(); } catch { /* always client-side */ }
    logout();
    navigate('/admin/login');
    toast.success('Çıkış yapıldı.');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen bg-[#0a0a0a] text-white overflow-hidden">

      {/* ── Mobile top bar (hidden on lg) ─────────────────────────── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-[#0d0d0d] border-b border-white/[0.06] flex items-center justify-between px-4 flex-shrink-0">
        <img
          src={logoSrc}
          alt="Ober Yapı"
          className="h-7 w-auto object-contain"
          style={{ filter: 'brightness(0.92) drop-shadow(0 1px 4px rgba(0,0,0,0.7))' }}
        />
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/[0.05] transition-colors"
          aria-label="Menüyü aç"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* ── Backdrop (mobile only) ─────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/65 backdrop-blur-[2px]"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ───────────────────────────────────────────────── */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
          w-64 lg:w-60 flex-shrink-0
          bg-[#0d0d0d] border-r border-white/[0.06] flex flex-col
          transition-transform duration-300 ease-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Brand */}
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img
                src={logoSrc}
                alt="Ober Yapı"
                className="h-8 w-auto object-contain"
                style={{ filter: 'brightness(0.92) drop-shadow(0 1px 4px rgba(0,0,0,0.7))' }}
              />
              <span className="text-white/25 text-[9px] uppercase tracking-[0.2em] font-medium border border-white/10 px-1.5 py-0.5 rounded">Admin</span>
            </div>
            {/* Close button — mobile only */}
            <button
              className="lg:hidden p-1.5 rounded-lg text-white/35 hover:text-white/70 hover:bg-white/[0.05] transition-colors"
              onClick={closeSidebar}
              aria-label="Menüyü kapat"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Nav with notification badges */}
        <SidebarNav closeSidebar={closeSidebar} />

        {/* Logout */}
        <div className="px-2.5 py-3 border-t border-white/[0.06]">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/35 hover:text-red-400 hover:bg-red-500/[0.07] transition-all duration-150"
          >
            <LogOut size={15} />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* ── Page content ──────────────────────────────────────────── */}
      {/* pt-14 on mobile to clear the fixed top bar */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <NotificationProvider>
      <AdminLayoutInner />
    </NotificationProvider>
  );
}
